
import { Component, Vue, Prop, Emit } from "vue-property-decorator";
import draggable from "vuedraggable";
import { Header } from "@/api/export";

interface FormData {
  fileName: string;
  suffix: string;
  questionType: number[];
  appendAsk: boolean;
  maxAskAppendRound: number;
}

@Component({
  components: {
    draggable
  }
})
export default class ExportVideoDrawer extends Vue {
  @Prop({ default: false })
  visible!: boolean;

  @Prop({ default: () => [] })
  selectedList!: any[];

  @Prop({ default: 0 })
  candidateIdsLength!: number;

  @Prop({ default: () => ({}) })
  candidateListForm!: any;

  @Prop({ default: () => [] })
  headers!: Header[];

  @Prop({ default: "selected" })
  exportMode!: "selected" | "all";

  // 缓存相关常量
  private readonly EXPORT_VIDEO_FIELDS_CACHE_KEY = "AIHR_EXPORT_VIDEO_FIELDS_CACHE";
  private readonly EXPORT_VIDEO_FIELDS_ORDER_CACHE_KEY =
    "AIHR_EXPORT_VIDEO_FIELDS_ORDER_CACHE";

  loading = false;
  leftSearchKey = "";
  rightSearchKey = "";

  formData: FormData = {
    fileName: "",
    suffix: "xlsx",
    questionType: [6], // 默认选中视频题
    appendAsk: true, // 默认选择是
    maxAskAppendRound: 3 // 默认3轮
  };

  rules = {
    fileName: [{ required: true, message: "请输入文件名", trigger: "blur" }],
    questionType: [
      { required: true, message: "请选择导出题目类型", trigger: "change" }
    ]
  };

  get selectedCount() {
    return this.selectedList.length || this.candidateIdsLength;
  }

  get showDifferentExamWarning() {
    // 仅在选中导出模式下判断
    if (this.exportMode !== "selected") {
      return false;
    }

    // 检查选中的候选人是否来自不同面试
    if (this.selectedList.length > 1) {
      const examIds = new Set(
        this.selectedList.map((item: any) => item.examId)
      );
      return examIds.size > 1; // 有多个不同的 examId
    }

    return false;
  }

  get hasVideoQuestion() {
    return this.formData.questionType.includes(6);
  }

  get availableFieldsCount() {
    return this.headers.length;
  }

  get filteredAllHeaders(): Header[] {
    if (!this.leftSearchKey) {
      return this.headers;
    }
    return this.headers.filter(field =>
      field.headerInExcel
        .toLowerCase()
        .includes(this.leftSearchKey.toLowerCase())
    );
  }

  get selectedFields() {
    return this.headers
      .filter(header => header.on)
      .sort((a, b) => {
        const orderA = (a as any)._sortOrder ?? 999999;
        const orderB = (b as any)._sortOrder ?? 999999;
        return orderA - orderB;
      });
  }

  get filteredSelectedFields(): Header[] {
    if (!this.rightSearchKey) {
      return this.selectedFields;
    }
    return this.selectedFields.filter(field =>
      field.headerInExcel
        .toLowerCase()
        .includes(this.rightSearchKey.toLowerCase())
    );
  }

  set filteredSelectedFields(newValue: Header[]) {
    // 拖拽排序时更新 _sortOrder 属性，不修改 headers 数组顺序
    newValue.forEach((field, index) => {
      this.$set(field, '_sortOrder', index);
    });
  }

  isAllFieldsSelected(): boolean {
    const fieldsToCheck = this.filteredAllHeaders;
    return fieldsToCheck.length > 0 && fieldsToCheck.every(field => field.on);
  }

  toggleAllFields() {
    const fieldsToToggle = this.filteredAllHeaders;
    const allSelected = this.isAllFieldsSelected();

    // 计算当前最大排序值
    let maxOrder = Math.max(
      ...this.selectedFields.map(f => (f as any)._sortOrder ?? 0),
      -1
    );

    fieldsToToggle.forEach(field => {
      field.on = !allSelected;
      // 新选中的字段分配排序
      if (!allSelected) {
        this.$set(field, '_sortOrder', ++maxOrder);
      }
    });
  }

  handleFieldToggle(header: Header) {
    // checkbox的change事件已经更新了header.on
    // 新选中的字段追加到末尾
    if (header.on) {
      const maxOrder = Math.max(
        ...this.selectedFields.map(f => (f as any)._sortOrder ?? 0),
        -1
      );
      this.$set(header, '_sortOrder', maxOrder + 1);
    }
  }

  removeSelectedField(header: Header) {
    header.on = false;
  }

  clearAllSelected() {
    this.headers.forEach(header => {
      header.on = false;
    });
  }

  handleDragChange() {
    // 拖拽排序已在 filteredSelectedFields 的 setter 中处理
  }

  // 缓存工具方法
  private saveFieldsCache(selectedTypes: string[]) {
    try {
      const cache: Record<string, boolean> = {};
      selectedTypes.forEach(type => {
        cache[type] = true;
      });
      localStorage.setItem(
        this.EXPORT_VIDEO_FIELDS_CACHE_KEY,
        JSON.stringify(cache)
      );
    } catch (error) {
      // 缓存保存失败，静默处理
      console.warn("保存答题明细字段缓存失败:", error);
    }
  }

  private saveFieldsOrderCache(selectedTypes: string[]) {
    try {
      localStorage.setItem(
        this.EXPORT_VIDEO_FIELDS_ORDER_CACHE_KEY,
        JSON.stringify(selectedTypes)
      );
    } catch (error) {
      // 缓存保存失败，静默处理
      console.warn("保存答题明细字段排序缓存失败:", error);
    }
  }

  private getFieldsCache(): Record<string, boolean> | null {
    try {
      const cacheStr = localStorage.getItem(
        this.EXPORT_VIDEO_FIELDS_CACHE_KEY
      );
      return cacheStr ? JSON.parse(cacheStr) : null;
    } catch (error) {
      console.warn("读取答题明细字段缓存失败:", error);
      return null;
    }
  }

  private getFieldsOrderCache(): string[] | null {
    try {
      const cacheStr = localStorage.getItem(
        this.EXPORT_VIDEO_FIELDS_ORDER_CACHE_KEY
      );
      return cacheStr ? JSON.parse(cacheStr) : null;
    } catch (error) {
      console.warn("读取答题明细字段排序缓存失败:", error);
      return null;
    }
  }

  // 应用缓存的字段选择
  private applyCachedFieldsSelection() {
    try {
      const fieldsCache = this.getFieldsCache();
      const orderCache = this.getFieldsOrderCache();

      // 如果没有缓存，直接使用后端返回的默认值，不做任何处理
      if (!fieldsCache || !orderCache || this.headers.length === 0) {
        return;
      }

      // 有缓存 - 完全覆盖后端的默认选中状态
      this.headers.forEach(header => {
        // 如果缓存中有这个字段，使用缓存值；否则设为 false
        header.on = fieldsCache[header.headerInSql] || false;
      });

      // 应用排序缓存 - 给每个字段添加排序信息
      orderCache.forEach((headerInSql, index) => {
        const header = this.headers.find(h => h.headerInSql === headerInSql);
        if (header && header.on) {
          // 动态添加排序属性（用于右侧已选字段排序）
          this.$set(header, '_sortOrder', index);
        }
      });

      // 给缓存中没有的新字段设置排序（追加到末尾）
      let maxOrder = orderCache.length;
      this.headers.forEach(header => {
        if (header.on && (header as any)._sortOrder === undefined) {
          this.$set(header, '_sortOrder', maxOrder++);
        }
      });

      console.log(
        "答题明细缓存应用成功，已选字段数:",
        this.headers.filter(h => h.on).length
      );
    } catch (error) {
      console.warn("应用答题明细缓存失败:", error);
      // 缓存应用失败不影响正常使用
    }
  }

  initFormData() {
    const now = new Date();
    const dateStr =
      now.getFullYear().toString() +
      (now.getMonth() + 1).toString().padStart(2, "0") +
      now
        .getDate()
        .toString()
        .padStart(2, "0");

    this.formData = {
      fileName: `AI面试导出候选人答题明细-${dateStr}`,
      suffix: "xlsx",
      questionType: [6], // 默认选中视频题
      appendAsk: true, // 默认选择是
      maxAskAppendRound: 3 // 默认3轮
    };

    // 重置表单验证
    this.$nextTick(() => {
      (this.$refs.exportForm as any)?.clearValidate();
    });
  }

  @Emit("close")
  handleClose() {
    return;
  }

  @Emit("confirm")
  handleConfirm() {
    return new Promise((resolve, reject) => {
      (this.$refs.exportForm as any).validate((valid: boolean) => {
        if (valid) {
          if (this.selectedFields.length === 0) {
            this.$message.error("请至少选择一个导出字段");
            reject(new Error("请至少选择一个导出字段"));
            return;
          }

          // 保存字段选择和排序到缓存
          const exportFields = this.selectedFields.map(
            field => field.headerInSql
          );
          this.saveFieldsCache(exportFields);
          this.saveFieldsOrderCache(exportFields);

          const exportParams = {
            fileName: this.formData.fileName.trim(),
            suffix: this.formData.suffix,
            questionType: this.formData.questionType,
            appendAsk: this.formData.appendAsk,
            maxAskAppendRound: this.formData.maxAskAppendRound,
            headersInSql: exportFields
          };

          resolve(exportParams);
        } else {
          this.$message.error("请检查表单填写");
          reject(new Error("表单验证失败"));
        }
      });
    });
  }

  mounted() {
    // 监听visible变化，重置表单
    this.$watch("visible", newVal => {
      if (newVal) {
        this.initFormData();
        this.leftSearchKey = "";
        this.rightSearchKey = "";

        // 应用缓存（如果存在）
        this.applyCachedFieldsSelection();

        // 重置滚动位置
        this.$nextTick(() => {
          if (this.$refs.leftFieldsList) {
            (this.$refs.leftFieldsList as HTMLElement).scrollTop = 0;
          }
          if (this.$refs.rightFieldsList) {
            (this.$refs.rightFieldsList as HTMLElement).scrollTop = 0;
          }
        });
      }
    });

    // 监听题目类型变化
    this.$watch("formData.questionType", newValue => {
      if (!newValue.includes(6)) {
        this.formData.appendAsk = true;
      }
    });
  }
}
